import { useCallback, useEffect, useState } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { sellerProtectionScreenViewEvent } from '@marketplace-web/seller-protection/seller-protection-data'

type UseSellerProtectionScreenTrackingProps = {
  transactionStatus: number | null | undefined
  shipmentStatus: number | null | undefined
  isVisible: boolean
}

type ViewScreenSessionData = {
  startTime: number
  isActive: boolean
}

export const useSellerProtectionScreenTracking = ({
  transactionStatus,
  shipmentStatus,
  isVisible,
}: UseSellerProtectionScreenTrackingProps) => {
  const { track } = useTracking()
  const [viewScreenSessionData, setViewScreenSessionData] = useState<ViewScreenSessionData | null>(
    null,
  )

  const trackViewEvent = useCallback(
    (timeSpentInScreenMS: number) => {
      if (timeSpentInScreenMS <= 0) return

      track(
        sellerProtectionScreenViewEvent({
          details: JSON.stringify({
            transaction_status: transactionStatus,
            shipment_status: shipmentStatus,
          }),
          timeSpentInScreenMS: timeSpentInScreenMS.toString(),
        }),
      )
    },
    [track, transactionStatus, shipmentStatus],
  )

  const finishSellerProtectionScreenViewing = useCallback(() => {
    if (!viewScreenSessionData || !viewScreenSessionData.isActive) return

    const timeSpentInMs = Date.now() - viewScreenSessionData.startTime
    trackViewEvent(timeSpentInMs)
    setViewScreenSessionData(prev => (prev ? { ...prev, isActive: false } : null))
  }, [viewScreenSessionData, trackViewEvent])

  useEffect(() => {
    if (isVisible && (!viewScreenSessionData || !viewScreenSessionData.isActive)) {
      setViewScreenSessionData({ startTime: Date.now(), isActive: true })

      return
    }

    if (!isVisible && viewScreenSessionData && viewScreenSessionData.isActive) {
      finishSellerProtectionScreenViewing()
    }
  }, [isVisible, viewScreenSessionData, finishSellerProtectionScreenViewing])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        finishSellerProtectionScreenViewing()

        return
      }

      if (document.visibilityState === 'visible' && isVisible) {
        setViewScreenSessionData({ startTime: Date.now(), isActive: true })
      }
    }

    if (isVisible) {
      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }

    return undefined
  }, [isVisible, finishSellerProtectionScreenViewing])

  return {
    finishSellerProtectionScreenViewing,
  }
}
